import { styled } from '@mui/material/styles';
import type { ReactElement, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { tokens } from '../../design';

/**
 * Single sidebar nav row. Matches `.sb__item` from layout.html:
 *   - inactive: rgba(255,255,255,0.7) text
 *   - hover:    rgba(255,255,255,0.05) bg + white text
 *   - active:   green gradient bg + bold + 1px inset border
 *
 * Active state is driven by NavLink's `isActive` callback so the URL
 * is the single source of truth — no parallel React state.
 *
 * `disabled` rows render as plain spans (no NavLink) so the URL bar
 * doesn't change and the cursor stays default — used for "coming soon"
 * destinations whose pages ship in later admin tickets (LOCK 11).
 */
export type SidebarItemProps = {
  to?: string;
  icon: ReactNode;
  label: string;
  /** Sidebar badge slot — empty/undefined hides the badge (LOCK 13). */
  badgeCount?: number | undefined;
  /**
   * Hover description of what the badge counts. Optional, but a badge
   * whose predicate is not obvious from the nav label should carry one —
   * an unexplained number that nobody can act on teaches people to stop
   * reading badges.
   */
  badgeTitle?: string | undefined;
  /** When true, renders as static non-link row (placeholder destination). */
  disabled?: boolean;
};

const itemBase = {
  position: 'relative' as const,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 18px',
  margin: '0 10px',
  borderRadius: 8,
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: 13,
  textDecoration: 'none' as const,
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
  },
};

const activeStyle = {
  background: `linear-gradient(90deg, ${tokens.brand.primary}29 0%, ${tokens.brand.primary}10 100%)`,
  color: '#ffffff',
  fontWeight: 600,
  boxShadow: `inset 0 0 0 1px ${tokens.brand.primary}38`,
};

const StyledNavLink = styled(NavLink)({
  ...itemBase,
  '&.active': activeStyle,
});

const StyledDisabledRow = styled('span')({
  ...itemBase,
  cursor: 'default',
  opacity: 0.6,
  '&:hover': {
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.7)',
  },
});

const Badge = styled('span')({
  marginLeft: 'auto',
  background: 'rgba(255, 255, 255, 0.08)',
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: 10,
  fontWeight: 700,
  fontFamily: tokens.font.mono,
  padding: '1px 6px',
  borderRadius: 999,
});

const IconWrap = styled('span')({
  width: 16,
  height: 16,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    width: 16,
    height: 16,
    stroke: 'currentColor',
    fill: 'none',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
});

export const SidebarItem = ({
  to,
  icon,
  label,
  badgeCount,
  badgeTitle,
  disabled,
}: SidebarItemProps): ReactElement => {
  const showBadge = typeof badgeCount === 'number' && badgeCount > 0;
  const badge = showBadge ? (
    <Badge {...(badgeTitle ? { title: badgeTitle } : {})}>{badgeCount}</Badge>
  ) : null;

  if (disabled || !to) {
    return (
      <StyledDisabledRow aria-disabled="true">
        <IconWrap>{icon}</IconWrap>
        {label}
        {badge}
      </StyledDisabledRow>
    );
  }

  return (
    <StyledNavLink to={to}>
      <IconWrap>{icon}</IconWrap>
      {label}
      {badge}
    </StyledNavLink>
  );
};
